import { useCallback, useEffect, useRef, useState } from "react";
import { getTenantAuth } from "../Auth/TokenManager";
import { GetMailboxHealth, MailboxHealthCard } from "./MailboxAPI";

/**
 * The five ways this can end, plus loading.
 *
 * `notEntitled` and `forbidden` are SEPARATE and must stay separate. The
 * first means the STS refused to elevate this admin into that tenant; the
 * second means the elevation worked and the tenant's own API still said no.
 * They indict different things and an operator needs to tell them apart —
 * the same reasoning that keeps the Core view's `Failing` and `Stale` from
 * collapsing into one badge.
 *
 * There is no `idle`: this hook is only ever called with a real tenant key,
 * because the view it backs exists only while a tenant is being inspected.
 */
export type InspectionState = "loading" | "ready" | "notEntitled" | "unauthorised" | "forbidden" | "failed";

export interface iUseTenantInspection {
    state: InspectionState;
    mailboxes: MailboxHealthCard[];
    /**
     * The scoped token from the most recent successful mint.
     *
     * VALID ONLY WHILE `state` is "ready". It is cleared when a new load
     * starts, so it is never the previous tenant's token. A caller must handle
     * its absence rather than assuming a token is always available.
     *
     * Presence is NOT a proxy for readiness: `setToken` runs after the mint but
     * before the fetch, so throughout the fetch leg this is defined while
     * `state` is still "loading". Read `state`.
     *
     * Spec §3.2's "never reused across a gap" is a property of this hook's own
     * mint-then-fetch, NOT of this field: a caller that holds this value across
     * a gap owns the expiry problem this hook deleted for itself. Mint fresh
     * per operation, as `load` does.
     *
     * SUB-PROJECT B/C SURFACE: held rather than discarded so email search and
     * email detail can read it without restructuring this hook. A string,
     * never a `selectedTenant` — see mintInspectionToken.
     */
    token: string | undefined;
    /**
     * True while a refresh is re-reading over an already-loaded view.
     *
     * Distinct from `state === "loading"`, which means there is nothing to
     * show yet. A refresh deliberately keeps the previous mailboxes and the
     * "ready" state visible — this is a diagnostic view, and an operator
     * refreshing it while watching a queue drain should not lose the table on
     * every click. Phase 1's useTenantOverview makes the same choice for the
     * same reason: it retains its last overview rather than blanking it.
     */
    refreshing: boolean;
    refresh: () => Promise<void>;
}

/**
 * Mints a scoped token for INSPECTION only.
 *
 * Returns the token string alone, deliberately, and is NOT exported.
 * `getTenantAuth` returns a `selectedTenant`, which is precisely the shape
 * `setSelectedTenant` accepts — and `tenantContext.selectedTenant` is the
 * APP-WIDE tenant that the page header and every tenant-scoped view read.
 * Nothing in Main.tsx re-fetches in response to it changing: Departments,
 * Mailboxes and Actions are loaded inside `selectTenantHandler` BEFORE
 * `setSelectedTenant` is called. So setting it from here would not trigger a
 * visible reload — it would leave the app pointing at one tenant while those
 * three lists still hold the previous tenant's data. A silent mismatch is
 * harder to notice than a reload, which is why this is guarded structurally
 * rather than by convention.
 * Phase 1 spec §9 records this trap; a test in this file pins the rule.
 *
 * NOTE: `getTenantAuth` THROWS rather than returning a status — bare
 * `Error("403")` and `Error("401")` message strings, not typed errors.
 * `selectTenantHandler` in `src/Main/Main.tsx` calls it with no try/catch at
 * all, so there is no existing pattern to copy. The caller below owns the
 * mapping. The never-throws
 * guarantee this hook relies on for the *fetch* side comes from
 * `GetMailboxHealth` (MailboxAPI.ts), which wraps `CallEmsApi` and returns a
 * status-0 `networkFailure()` instead of rejecting. `CallEmsApi` itself has
 * no such guarantee — it does not try/catch its `fetch` or `response.json()`
 * calls and rejects on a network error or a malformed body.
 */
async function mintInspectionToken(tenantKey: string): Promise<string> {
    const auth = await getTenantAuth(tenantKey);
    return auth.token;
}

/** Maps `getTenantAuth`'s bare-message throws onto states. Anything unrecognised is a failure. */
function stateForMintError(error: unknown): InspectionState {
    if (error instanceof Error && error.message === "403") {
        return "notEntitled";
    }
    if (error instanceof Error && error.message === "401") {
        return "unauthorised";
    }
    return "failed";
}

function stateForFetchStatus(status: number): InspectionState {
    if (status === 403) {
        return "forbidden";
    }
    if (status === 401) {
        return "unauthorised";
    }
    return "failed";
}

/**
 * Reads one tenant's mailbox health with a freshly-minted scoped token.
 *
 * MINT AND FETCH ARE ONE OPERATION, deliberately. `Main.tsx`'s
 * `checkTenantToken` re-mints every 7 minutes against an "expires within 10
 * minutes" test, so these tokens are short-lived; a token minted when the
 * view opened and reused by a Refresh twenty minutes later would routinely be
 * dead. Minting on every fetch means the token is never reused across a gap,
 * which deletes the whole expiry failure class instead of handling it. The
 * cost is one extra STS request per manually-triggered refresh.
 */
export function useTenantInspection(tenantKey: string): iUseTenantInspection {
    const [state, setState] = useState<InspectionState>("loading");
    const [mailboxes, setMailboxes] = useState<MailboxHealthCard[]>([]);
    const [token, setToken] = useState<string | undefined>(undefined);
    const [refreshing, setRefreshing] = useState(false);

    // Bumped on entry and by the effect cleanup, and checked after every await.
    //
    // Only AFTER, deliberately. Phase 2's useTenantTrends checks before its
    // awaits too, because a pool worker there can pick up an item after
    // cancellation — but this hook has no such shape: `load` bumps the counter
    // itself on entry, so a check before its first await is always true. Two
    // such checks were removed for being dead conditions; do not reinstate them.
    const requestId = useRef(0);

    const load = useCallback(async (key: string, mode: "initial" | "refresh" = "initial") => {
        requestId.current += 1;
        const thisRequest = requestId.current;
        const isCurrent = () => requestId.current === thisRequest;

        // A refresh keeps the current state and mailboxes on screen; only an
        // initial load has nothing to show and drops to "loading". A failed
        // refresh still transitions to a failure state below — the stale
        // table is only kept for the in-flight window, never behind a
        // failure, where it would imply the data is current.
        if (mode === "refresh") {
            setRefreshing(true);
        } else {
            setState("loading");
        }
        setToken(undefined);

        try {
            let minted: string;
            try {
                minted = await mintInspectionToken(key);
                if (!isCurrent()) {
                    return;
                }
            } catch (error) {
                if (isCurrent()) {
                    setState(stateForMintError(error));
                }
                return;
            }

            setToken(minted);

            const response = await GetMailboxHealth(minted);
            if (!isCurrent()) {
                return;
            }

            if (response.isOk) {
                // `?? []` on a COLLECTION, which the conventions permit with a
                // reason: a 200 carrying no body and a 200 carrying `[]` are the
                // same answer — this tenant has no mailboxes configured. There is
                // no third state for the view to distinguish, unlike the Core
                // aggregates where absent and zero mean genuinely different
                // things.
                setMailboxes(response.data ?? []);
                setState("ready");
            } else {
                setState(stateForFetchStatus(response.status));
            }
        } finally {
            // try/finally rather than a check at each return above: there are
            // several exit paths out of the block, and a flag left stuck true
            // after any one of them would permanently disable the button.
            if (isCurrent()) {
                setRefreshing(false);
            }
        }
    }, []);

    useEffect(() => {
        void load(tenantKey);
        // Invalidates in-flight work on unmount. Deliberately kept even though
        // no test can observe it: on a tenantKey change `load`'s own entry-bump
        // already invalidates the previous call, and on unmount React 19
        // discards the setState silently rather than warning. It is retained
        // because that redundancy is contingent on `load` bumping on entry —
        // move that and this becomes load-bearing again, with nothing to
        // announce the change.
        return () => {
            requestId.current += 1;
        };
    }, [tenantKey, load]);

    const refresh = useCallback(async () => {
        await load(tenantKey, "refresh");
    }, [load, tenantKey]);

    return { state, mailboxes, token, refreshing, refresh };
}
